import { authClient } from "@/lib/auth-client";
import { api } from "@/lib/api";
import {
  createRootRoute,
  createRoute,
  createRouter,
  notFound,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DashboardLayout } from "./pages/DashboardLayout";
import { LoginPage } from "./pages/LoginPage";
import { OnboardingLayout } from "./pages/onboarding/OnboardingLayout";
import { OrganizationStep } from "./pages/onboarding/OrganizationStep";
import { ProvisioningStep } from "./pages/onboarding/ProvisioningStep";
import { ConnectStep } from "./pages/onboarding/ConnectStep";
import { WelcomeStep } from "./pages/onboarding/WelcomeStep";
import { SignupPage } from "./pages/SignupPage";
import { VerifyPage } from "./pages/VerifyPage";
import { FinishSetupPage } from "./pages/FinishSetupPage";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminOrgsPage } from "./pages/admin/OrganizationsPage";
import { AdminOrgDetailPage } from "./pages/admin/OrgDetailPage";
import { AdminPermissionsPage } from "./pages/admin/PermissionsDashboardPage";
import { SubdomainAdminPage } from "./pages/admin/SubdomainAdmin";
import { AdminUsersPage } from "./pages/admin/UsersPage";
import { AdminInfrastructurePage } from "./pages/admin/InfrastructurePage";
import { AdminContainerDetailPage } from "./pages/admin/ContainerDetailPage";
import { AdminCostsPage } from "./pages/admin/CostsPage";
import { wantsCosts } from "./lib/show-costs";
import { AdminLLMUsagePage } from "./pages/admin/LLMUsagePage";
import { AdminGchatCustomPage } from "./pages/admin/GchatCustomPage";
import { AdminSlackConnectionsPage } from "./pages/admin/SlackConnectionsPage";
import { AdminNamedWarmPoolPage } from "./pages/admin/NamedWarmPoolPage";
import { AdminWaitlistPage } from "./pages/admin/WaitlistPage";
import { AdminBillingGrantsPage } from "./pages/admin/BillingGrantsPage";
import { AdminBillingConfigPage } from "./pages/admin/BillingConfigPage";
import { MockCheckoutPage } from "./pages/MockCheckoutPage";
import { AdminAgentKeysPage } from "./pages/admin/AgentKeysPage";
import { AdminExperimentsPage } from "./pages/admin/ExperimentsPage";
import { ContainersDashboardPage } from "./pages/ContainersDashboardPage";
import { ContainerLayout } from "./pages/ContainerLayout";
import { BrainChatTab } from "./pages/container-tabs/BrainChatTab";
import { LogsTab } from "./pages/container-tabs/LogsTab";
import { ActivityTab } from "./pages/container-tabs/ActivityTab";
import { UsageTab } from "./pages/container-tabs/UsageTab";
import { ConnectionsTab } from "./pages/container-tabs/connections/ConnectionsTab";
import { ConnectionPage } from "./pages/container-tabs/connections/ConnectionPage";
import { IntegrationsTab } from "./pages/container-tabs/IntegrationsTab";
import { ConnectorPage } from "./pages/container-tabs/integrations/connectors/ConnectorPage";
import { CONNECTOR_TABS, type ConnectorTab } from "./lib/connectors";
import { ChannelsTab } from "./pages/container-tabs/ChannelsTab";
import { AccessRoute, CanUseRoute, ExtrasRoute, PeopleRoute } from "./pages/container-tabs/people/routes";
import { type SharingSearch, SharingTab } from "./pages/container-tabs/SharingTab";
import { type AccessSearch, AccessPage } from "./pages/access/AccessPage";
import { AuditLogPage } from "./pages/audit/AuditLogPage";
import { parseAuditSearch } from "./lib/audit-search";
import { AppsTab } from "./pages/container-tabs/AppsTab";
import { APPS_TABS, type AppsSubTab } from "./lib/bundle-state";
import { VaultGate } from "./pages/container-tabs/VaultGate";
import { MembersPage } from "./pages/MembersPage";
import { OrganizationSettingsPage } from "./pages/OrganizationSettingsPage";
import { OrgConnectionDetailPage, OrgConnectionsPage, OrgIntegrationsPage, OrgModelsPage } from "./pages/OrgPages";
import { ModelsTab } from "./pages/container-tabs/models/ModelsTab";
import { ManageIntegrationPage } from "./pages/ManageIntegrationPage";
import { AcceptInvitationPage } from "./pages/AcceptInvitationPage";
import { UsagePage } from "./pages/UsagePage";
import { validateCheckoutReturn, validateUsageSearch } from "./lib/usage-tabs";
import { validatePeopleSearch } from "./lib/people-tabs";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { TermsAndConditionsPage } from "./pages/TermsAndConditionsPage";
import { OrgPickerRedirect } from "./pages/OrgPickerRedirect";
import { resolveRootRedirect } from "./lib/rootRedirect";
import { ErrorView } from "./components/ErrorView";
import { NoAccessPage } from "./pages/NoAccessPage";
import { ConnectorLinkPage } from "./pages/ConnectorLinkPage";
import { NotFoundPage } from "./components/NotFoundPage";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { BannedPage } from "./pages/BannedPage";

// ============================================================================
// Route guards
// ----------------------------------------------------------------------------
// Composable, single-purpose async guards used as `beforeLoad` on layout routes.
// Children of a guarded route inherit the check — no need to repeat per page.
// ============================================================================

type SessionUser = {
  id: string;
  role?: string | null;
  onboarding?: boolean;
  banned?: boolean | null;
  banReason?: string | null;
  banExpires?: string | null;
};

function checkBan(user: SessionUser) {
  if (!user.banned) return;
  const expires = user.banExpires ? new Date(user.banExpires) : null;
  if (expires && expires <= new Date()) return;
  throw redirect({
    to: "/banned",
    search: {
      reason: user.banReason ?? undefined,
      expires: user.banExpires ?? undefined,
    },
  });
}

function isRedirect(err: unknown): boolean {
  return err instanceof Response || typeof (err as { to?: unknown })?.to === "string";
}

/** Redirects to /login when no session. Stores `{ session }` on route context. */
async function requireAuth({ location }: { location: { href: string } }) {
  const next = location.href === "/" ? undefined : location.href;
  try {
    const { data } = await authClient.getSession();
    if (!data?.user) throw redirect({ to: "/login", search: next ? { next } : {} });
    checkBan(data.user as SessionUser);
    if ((data.user as SessionUser).onboarding) throw redirect({ to: "/onboarding" });
    return { session: data };
  } catch (err) {
    if (isRedirect(err)) throw err;
    throw redirect({ to: "/login", search: next ? { next } : {} });
  }
}

/** Requires auth but allows onboarding users (used by the onboarding route itself). */
async function requireAuthAllowOnboarding() {
  try {
    const { data } = await authClient.getSession();
    if (!data?.user) throw redirect({ to: "/login" });
    checkBan(data.user as SessionUser);
    return { session: data };
  } catch (err) {
    if (isRedirect(err)) throw err;
    throw redirect({ to: "/login" });
  }
}

/** Silently checks auth + admin role; throws 404 for any failure (no login hint). */
async function requireAdminOrNotFound() {
  let session;
  try {
    const { data } = await authClient.getSession();
    session = data;
  } catch {
    throw notFound();
  }
  if (!session?.user) throw notFound();
  checkBan(session.user as SessionUser);
  if ((session.user as SessionUser).role !== "super_admin") throw notFound();
  return { session };
}

// ============================================================================
// Routes
// ============================================================================

const rootRoute = createRootRoute({ component: () => <Outlet /> });

// --- Public routes ---------------------------------------------------------

function validateNextSearch(search: Record<string, unknown>): { next?: string; email?: string } {
  const out: { next?: string; email?: string } = {};
  if (search.next && typeof search.next === "string") out.next = search.next;
  if (search.email && typeof search.email === "string") out.email = search.email;
  return out;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: validateNextSearch,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
  validateSearch: validateNextSearch,
});

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/verify",
  component: VerifyPage,
  validateSearch: (search: Record<string, unknown>) => ({
    email: typeof search.email === "string" ? search.email : undefined,
    otp: typeof search.otp === "string" ? search.otp : undefined,
  }),
});

// Landing for the waitlist-approval email's "Finish setup" CTA. Public (it
// decides itself whether to send the visitor to /login or /onboarding) and
// identity-aware: ?email= is the approved address, used to prefill + lock the
// login and to catch "signed in as someone else" instead of silently landing
// on that account's dashboard.
const finishSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/finish-setup",
  component: FinishSetupPage,
  validateSearch: (search: Record<string, unknown>) => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
});

const onboardingLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: OnboardingLayout,
  beforeLoad: requireAuthAllowOnboarding,
});

// All onboarding gating now lives in the layout: it queries
// /api/onboarding/state on every mount and redirects users to the
// step the backend says they're on. Per-route beforeLoads have been
// removed so the auth layer is in exactly one place. The index route
// renders nothing on its own — the layout effect bounces it to the
// authoritative step on first paint.
const onboardingIndexRoute = createRoute({
  getParentRoute: () => onboardingLayout,
  path: "/",
  component: () => null,
});

const onboardingWelcomeRoute = createRoute({
  getParentRoute: () => onboardingLayout,
  path: "/welcome",
  component: WelcomeStep,
});

const onboardingOrgRoute = createRoute({
  getParentRoute: () => onboardingLayout,
  path: "/organization",
  component: OrganizationStep,
});

// A hub connect started here returns with ?integrations=<outcome>; declared so
// the router keeps it through init (see onboardingConnectRoute).
const connectOutcomeSearch = (search: Record<string, unknown>): { integrations?: string } =>
  typeof search.integrations === "string" ? { integrations: search.integrations } : {};

const onboardingProvisioningRoute = createRoute({
  getParentRoute: () => onboardingLayout,
  path: "/provisioning",
  component: ProvisioningStep,
  validateSearch: connectOutcomeSearch,
});

const onboardingConnectRoute = createRoute({
  getParentRoute: () => onboardingLayout,
  path: "/connect",
  component: ConnectStep,
  // Preserve the Slack/Google Chat OAuth-callback params through router
  // init. Without a validateSearch, the router strips these undeclared
  // params from the URL on load — and since ConnectStep is gated behind
  // the layout's /state loader, it mounts *after* the strip and reads a
  // bare URL. The `?slack=connected` success short-circuit then misses and
  // the page falls back to (possibly lagging) server state, flashing the
  // connect buttons before the "Boo is live" screen (BOO-309).
  validateSearch: (search: Record<string, unknown>) => {
    const out: { slack?: string; gchat?: string; workspace?: string } = {};
    if (typeof search.slack === "string") out.slack = search.slack;
    if (typeof search.gchat === "string") out.gchat = search.gchat;
    if (typeof search.workspace === "string") out.workspace = search.workspace;
    return out;
  },
});

const bannedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/banned",
  component: BannedPage,
  validateSearch: (search: Record<string, unknown>) => ({
    reason: typeof search.reason === "string" ? search.reason : undefined,
    expires: typeof search.expires === "string" ? search.expires : undefined,
  }),
});

const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invitation/$invitationId",
  component: AcceptInvitationPage,
});

// Public legal pages — no auth, no redirect-if-authenticated. Linked from
// the auth screens and footer; also expected by external integrations
// (e.g. OAuth consent screens).
const privacyPolicyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy-policy",
  component: PrivacyPolicyPage,
});



const termsAndConditionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms-and-conditions",
  component: TermsAndConditionsPage,
});

// The one-time link Boo sends one person to fill in a connector's connection.
// Public: the token is the credential, so no sign-in and no dashboard chrome.
// The page drops the token from the address once read, staying on this route.
const connectorLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect/connector/{-$token}",
  component: ConnectorLinkPage,
});

// --- Authenticated layout (everything below requires a logged-in user) ----

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  beforeLoad: requireAuth,
  component: DashboardLayout,
});

// Every customer-facing dashboard page is parented under `/orgs/$orgId`. The
// segment is the source of truth for "which org am I looking at" — opening
// a deep link with someone else's orgId switches the active org before
// rendering, so multi-org users land where the link actually points
// (BOO-190 fix). Admin pages stay flat since they aren't org-scoped.
const orgRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "orgs/$orgId",
  component: () => <Outlet />,
  beforeLoad: async ({ params }) => {
    const { orgId } = params as { orgId: string };
    let data;
    try {
      ({ data } = await authClient.getSession());
    } catch {
      // Server momentarily unreachable (e.g. the laptop just woke). Proceed and
      // let the route's own queries render/retry, rather than throwing to the
      // fullscreen error boundary and wiping the view the user was looking at.
      return;
    }
    if (!data?.session) return; // requireAuth above handles the no-session redirect.
    if (data.session.activeOrganizationId === orgId) return;

    let result;
    try {
      result = await authClient.organization.setActive({ organizationId: orgId });
    } catch {
      // Network hiccup switching active org — proceed rather than error out the
      // whole view. On revalidation the active org is usually already correct.
      return;
    }
    if (result.error) {
      // 403 from the auth-service: the viewer isn't a member of the target org.
      // (Super-admins are allowed into any org by the auth-service, so they
      // never reach here — they land on the org's view as "super admin".)
      // Show a plain no-access page rather than silently swapping the viewer's
      // active org and bouncing them to their own dashboard — that silent
      // bounce is what read as "the credits email links to the wrong org".
      // Carry the target org id through so the page can name which org the
      // link pointed at (we can't resolve its name here — the viewer isn't a
      // member — but the id lets them quote it when asking to be invited).
      throw redirect({ to: "/no-access", search: { org: orgId } });
    }
  },
});

// `/orgs/$orgId` is the containers dashboard (the new home).
const dashboardRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "/",
  component: ContainersDashboardPage,
});

// Container-scoped subtree. Everything chat/inspect/logs/usage/integrations
// related now lives under /$orgId/containers/$containerId/<tab>. The layout
// owns the header (name + status + actions) and tab strip; the tab routes
// only render their inner content.
const containerLayoutRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "containers/$containerId",
  component: ContainerLayout,
});

// Connections is the container's home: it renders right at
// `/$orgId/containers/$containerId` (the index), not a sub-path. Every other
// tab lives at `.../<tab>`. This means a bare container link lands on a real,
// always-visible page directly — no default-tab redirect, no bounce.
const containerIndexRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/",
  component: ConnectionsTab,
});

// One connection's page (Slack, GitHub, Microsoft 365, ...), opened from the
// Connections home. Under the Connections tab in the tab strip.
const containerConnectionRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/connections/$connection",
  component: ConnectionPage,
});

// "Chat" is the two-way dashboard chat with the brain (super-admin only; tab
// hidden and backend-gated otherwise). `thread` selects a conversation; absent
// = new.
const containerChatRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/chat",
  component: BrainChatTab,
  // `costs=true` reveals per-session spend in the Chat tab (super-admin
  // only, and only when asked for — it's an internal diagnostic, not part of
  // the everyday view).
  validateSearch: (search: Record<string, unknown>): { thread?: string; costs?: boolean } => ({
    ...(typeof search.thread === "string" && search.thread ? { thread: search.thread } : {}),
    ...(wantsCosts(search) ? { costs: true } : {}),
  }),
});

// Back-compat: the Chat tab used to live at /brain-chat. Redirect old
// super-admin bookmarks (and any brain-minted links) to the new /chat path.
const containerBrainChatRedirectRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/brain-chat",
  beforeLoad: ({ params }) => {
    const { orgId, containerId } = params as { orgId: string; containerId: string };
    throw redirect({
      to: "/orgs/$orgId/containers/$containerId/chat",
      params: { orgId, containerId },
    });
  },
  component: () => null,
});

const containerLogsRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/logs",
  component: LogsTab,
});

const containerUsageRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/usage",
  component: UsageTab,
  // Spend-panel state lives in the URL so browser Back/Forward walk filter
  // steps.
  validateSearch: (search: Record<string, unknown>) => {
    const out: {
      uPeriod?: string;
      uUser?: string;
      uChannel?: string;
      uPurpose?: string;
      uThread?: string;
    } = {};
    for (const key of [
      "uPeriod",
      "uUser",
      "uChannel",
      "uPurpose",
      "uThread",
    ] as const) {
      const value = search[key];
      if (typeof value === "string" && value.length > 0) out[key] = value;
    }
    return out;
  },
});

// The home was the General tab at `/general`. Keep it as a redirect so
// pre-existing links (OAuth returnTo paths, bookmarks) land on the Connections
// home instead of 404ing. The query string comes along:
// connect flows report their outcome in it, and the Connections home handles
// it. Microsoft 365 flows go on to their connection's page, which handles theirs.
const containerGeneralRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/general",
  beforeLoad: ({ params, location }) => {
    const { orgId, containerId } = params as { orgId: string; containerId: string };
    const search = location.search as Record<string, unknown>;
    if ("ms365" in search) {
      throw redirect({
        to: "/orgs/$orgId/containers/$containerId/connections/$connection",
        params: { orgId, containerId, connection: "microsoft-365" },
        search,
      });
    }
    throw redirect({
      to: "/orgs/$orgId/containers/$containerId",
      params: { orgId, containerId },
      search,
    });
  },
  component: () => null,
});

// Integrations hub (connected accounts + catalog browser). Brain-only —
// ContainerLayout hides the tab for other Boo types and bounces deep links.
const containerIntegrationsRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/integrations",
  component: IntegrationsTab,
  validateSearch: connectOutcomeSearch,
});

const connectorTabSearch = (search: Record<string, unknown>): { tab?: ConnectorTab } =>
  typeof search.tab === "string" && (CONNECTOR_TABS as readonly string[]).includes(search.tab)
    ? { tab: search.tab as ConnectorTab }
    : {};

// One connector Boo built (shown as a "custom integration"), opened from the
// Integrations tab (under it in the tab strip). `?tab=` picks the section;
// review shows only while a version waits. Boo's links use the org-less
// /containers/... form, resolved below.
const containerConnectorRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/integrations/custom/$slug",
  validateSearch: connectorTabSearch,
  component: ConnectorPage,
});

// Back-compat: the page used to live at /integrations/connectors/$slug, then
// /integrations/proxies/$slug, and Boo's messages still link to both.
const connectorRedirectRoute = (path: "/integrations/connectors/$slug" | "/integrations/proxies/$slug") =>
  createRoute({
    getParentRoute: () => containerLayoutRoute,
    path,
    validateSearch: connectorTabSearch,
    beforeLoad: ({ params, search }) => {
      const { orgId, containerId, slug } = params as { orgId: string; containerId: string; slug: string };
      throw redirect({
        to: "/orgs/$orgId/containers/$containerId/integrations/custom/$slug",
        params: { orgId, containerId, slug },
        search,
        replace: true,
      });
    },
    component: () => null,
  });
const containerConnectorRedirectRoute = connectorRedirectRoute("/integrations/connectors/$slug");
const containerProxyRedirectRoute = connectorRedirectRoute("/integrations/proxies/$slug");

// Installable apps (bundles). Brain-only, same gating as integrations.
// `?tab=` picks App store or App configuration; `?app=` opens one app's
// details in the App store.
const containerAppsRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/apps",
  validateSearch: (search: Record<string, unknown>): { tab?: AppsSubTab; app?: string } => ({
    ...(typeof search.tab === "string" && (APPS_TABS as readonly string[]).includes(search.tab)
      ? { tab: search.tab as AppsSubTab }
      : {}),
    ...(typeof search.app === "string" && search.app ? { app: search.app } : {}),
  }),
  component: AppsTab,
});

const containerChannelsRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/channels",
  component: ChannelsTab,
  // `costs=true` reveals the Spend column to a super admin where the customer
  // wouldn't see it (BOO-376).
  validateSearch: (search: Record<string, unknown>): { costs?: boolean } =>
    wantsCosts(search) ? { costs: true } : {},
});

function validateGroupSearch(search: Record<string, unknown>): { group?: string } {
  return typeof search.group === "string" && search.group ? { group: search.group } : {};
}

const containerPeopleRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/people",
  component: PeopleRoute,
  validateSearch: validatePeopleSearch,
});

const containerCanUseRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/can-use",
  component: CanUseRoute,
  validateSearch: validateGroupSearch,
});

const containerExtrasRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/extras",
  component: ExtrasRoute,
});

const containerSharingRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/sharing",
  component: SharingTab,
  // Deep links from chat and emails (see SharingTab); undeclared params are
  // stripped on load.
  validateSearch: (search: Record<string, unknown>): SharingSearch => {
    const out: SharingSearch = {};
    for (const k of ["request", "find", "source", "review"] as const) {
      const v = search[k];
      if (typeof v === "string" && v) out[k] = v;
      else if (typeof v === "number") out[k] = String(v);
    }
    return out;
  },
});

const containerAccessRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/access",
  component: AccessRoute,
});

// Which models this Boo runs (its own picks over the org defaults), plus its
// own endpoints when the org is enrolled.
const containerModelsRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/models",
  component: ModelsTab,
});

// One Boo's audit log, filters in the URL like the org Audit log's.
const containerActivityRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/activity",
  component: ActivityTab,
  validateSearch: parseAuditSearch,
});

const containerVaultRoute = createRoute({
  getParentRoute: () => containerLayoutRoute,
  path: "/vault",
  component: VaultGate,
});

// Flat top-level org pages, one per sidebar entry.
const membersRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "members",
  component: MembersPage,
});

// Every grant between the org's Boos, the access log, and handovers.
const accessRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "access",
  component: AccessPage,
  validateSearch: (search: Record<string, unknown>): AccessSearch => {
    const out: AccessSearch = {};
    if (search.tab === "grants" || search.tab === "activity" || search.tab === "handover") out.tab = search.tab;
    if (typeof search.link === "string") out.link = search.link;
    return out;
  },
});

// Every recorded action in the org, with filters in the URL (?category=access).
// The page itself tells people without canViewAudit that it's for owners and admins.
const auditRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "audit",
  component: AuditLogPage,
  validateSearch: parseAuditSearch,
});

// The org's name and logo.
const orgSettingsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "settings",
  component: OrganizationSettingsPage,
});

// The workspace's Connections: shared ones connected once, then given to Boos
// (Devin's Enterprise Settings → Connections plus Repository permissions).
const orgConnectionsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "connections",
  component: OrgConnectionsPage,
});

const orgConnectionRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "connections/$connection",
  component: OrgConnectionDetailPage,
});

const orgIntegrationsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "integrations",
  component: OrgIntegrationsPage,
  validateSearch: connectOutcomeSearch,
});

// The org's default models for every Boo in it.
const orgModelsRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "models",
  component: OrgModelsPage,
});

// Settings, Connections, Integrations and Models were tabs under one Manage
// page at /organization/<tab>. Bookmarks and connect flows started before the
// split (GitHub/GitLab returns to /organization/connections/<host>, hub
// connects to /organization/integrations?integrations=<outcome>) still come
// in on those paths: send each to its page, query string kept. The splat also
// matches the bare /organization (General, now Settings).
const legacyManageRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "organization/$",
  beforeLoad: ({ params, location }) => {
    const { orgId, _splat } = params as { orgId: string; _splat?: string };
    const parts = (_splat ?? "").split("/").filter(Boolean);
    const page =
      parts[0] === "connections"
        ? parts.slice(0, 2).join("/")
        : parts[0] === "integrations" || parts[0] === "models"
          ? parts[0]
          : "settings";
    throw redirect({ href: `/orgs/${orgId}/${page}${location.searchStr}` });
  },
  component: () => null,
});

// Scope-neutral: reached from both the org integrations tab and a Boo's
// integrations tab. Account-centric management (access, tools, disconnect).
// `?tab=` is validated + defaulted so the active tab is linkable/shareable.
// `?boo=` is the Boo the user came from: the back link returns there, the
// account list is fetched in that Boo's scope (a Boo-private account only
// shows up there), and "Add account" connects privately to it.
const manageIntegrationRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "integrations/$accountId",
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab: "tools" | "access"; boo?: string; integrations?: string } => ({
    tab: search.tab === "access" ? "access" : "tools",
    ...(typeof search.boo === "string" && search.boo ? { boo: search.boo } : {}),
    ...connectOutcomeSearch(search),
  }),
  component: ManageIntegrationPage,
});

// The org's usage and money page, one `?tab=` per section (Usage, Spending
// limits, Invoices, Credit balance). Also accepts the hosted-checkout return
// params (?checkout=success etc.) so the router doesn't strip them before the
// page can toast + refetch.
const usageRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "usage",
  component: UsagePage,
  validateSearch: validateUsageSearch,
});

// Billing moved into the Usage page's tabs. Kept as a redirect: Stripe's hosted
// checkout returns here with ?checkout= / ?setup=, and emails and old bookmarks
// still link here. The checkout params ride along so the Credit balance tab can
// toast and refetch; the People page's old #spending-limits link opens the
// Spending limits tab.
const billingRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "billing",
  validateSearch: validateCheckoutReturn,
  beforeLoad: ({ params, search, location }) => {
    const limits = location.hash === "spending-limits";
    throw redirect({
      to: "/orgs/$orgId/usage",
      params: { orgId: params.orgId },
      search: { tab: limits ? "limits" : "balance", ...search },
      ...(limits ? { hash: "spending-limits" } : {}),
      replace: true,
    });
  },
  component: () => null,
});

// Fake hosted-checkout page for the mock payment provider (BOO-331). The
// backend builds URLs of this shape; it dies once real Stripe hosting lands.
const mockCheckoutRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: "mock-checkout/$sessionId",
  component: MockCheckoutPage,
});

// --- Legacy flat root ------------------------------------------------------
// The bare `/` entrypoint still needs to resolve somewhere, so bounce it to the
// user's active org dashboard. The other flat pages (/billing, /usage,
// /members, /organization) are gone: org-scoped links now come in as
// /orgs/$orgId/<page> (canonical) or /organization/<page> (the picker sentinel, for
// links that don't know the org), so the active-org-guessing catch-alls aren't
// needed and were removed.

async function bounceToActiveOrg() {
  const { data } = await authClient.getSession();
  const active = data?.session?.activeOrganizationId ?? null;

  // Load the viewer's OWN memberships. If this fetch fails we genuinely can't
  // decide where to send them — and guessing is worse than useless here: honoring
  // an unverifiable `active` would dump a super-admin back into a stale foreign org
  // (the Madhur/Joy-Life bug), and falling back to /onboarding can loop an
  // onboarded user. So we let the error propagate to legacyRootRoute's
  // errorComponent, which shows a retryable "couldn't load your workspaces" screen.
  // The destination logic itself is the pure, unit-tested resolveRootRedirect.
  const orgs = await api.orgs.list();

  const dest = resolveRootRedirect(active, orgs);
  switch (dest.kind) {
    case "org":
      // The /orgs/$orgId guard re-runs setActive on arrival, healing a stale
      // foreign active org, so we don't set it here.
      throw redirect({ to: "/orgs/$orgId", params: { orgId: dest.orgId } });
    case "choose":
      // Dedicated full-screen picker route — not rendered inline here, which
      // would show the picker inside the dashboard shell with a sidebar bound
      // to a stale org.
      throw redirect({ to: "/choose-organization" });
    case "onboarding":
      throw redirect({ to: "/onboarding" });
  }
}

// Signed-in but the active account isn't a member of the org a deep link
// pointed at. Reached from the orgRoute guard. Parented on the root (not the
// dashboard layout) so it renders full-screen without a sidebar for an org the
// viewer can't see — its own `requireAuth` keeps it behind login so it can name
// the signed-in account.
const noAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/no-access",
  component: NoAccessPage,
  beforeLoad: requireAuth,
  validateSearch: (search: Record<string, unknown>): { org?: string } =>
    typeof search.org === "string" ? { org: search.org } : {},
});

const legacyRootRoute = createRoute({
  // Parented on the root, not protectedRoute, so the errorComponent renders
  // full-screen without the dashboard sidebar (which would be a "can't load
  // your workspaces" screen sitting behind a workspace nav). requireAuth is
  // composed in explicitly since we're no longer under protectedRoute.
  getParentRoute: () => rootRoute,
  path: "/",
  // Always redirects (to the active/own org, the picker, or onboarding), so the
  // component never actually renders on the happy path.
  beforeLoad: async (ctx) => {
    await requireAuth(ctx);
    await bounceToActiveOrg();
  },
  component: () => null,
  // If bounceToActiveOrg can't load the viewer's memberships, show a retryable
  // screen rather than guessing a destination. `reset` re-runs beforeLoad.
  errorComponent: ({ error, reset }) => (
    <ErrorView
      variant="fullscreen"
      title="Couldn't load your workspaces"
      error={error}
      onRetry={reset}
    />
  ),
});

// Org-less /containers/$containerId/<tab...> deep links (e.g. chat CTAs Brain
// mints without knowing the viewer's org) get resolved into the canonical
// org-prefixed form. A container maps to exactly one org, so we look the owning
// org up server-side from the id (GET /api/containers/:id/org) — the "lookup so
// the frontend doesn't have to cry" — rather than trusting the viewer's active
// org. The splat preserves the requested tab tail (channels, knowledge/apps,
// chat/$threadId); an empty tail lands on the container's Connections home (the
// index), and the query string is always kept, so nothing is dropped.
const legacyContainerRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/containers/$containerId/$",
  beforeLoad: async ({ params, location }) => {
    const { containerId, _splat } = params as { containerId: string; _splat?: string };
    // Empty tail → Connections home; otherwise the requested tab, with a leading /.
    const tail = _splat && _splat.length > 0 ? `/${_splat}` : "";

    let orgId: string;
    try {
      const res = await api.containers.resolveOrg(containerId);
      orgId = res.data.organizationId;
    } catch {
      // Not found / not a member / backend down — fall back to the viewer's
      // active org so the link still resolves rather than dead-ending.
      let active: string | null | undefined;
      try {
        const { data } = await authClient.getSession();
        active = data?.session?.activeOrganizationId;
      } catch {
        // Both the org lookup and the session fetch failed (server unreachable).
        // Land on onboarding as a safe redirect instead of throwing to the
        // fullscreen error boundary.
        throw redirect({ to: "/onboarding" });
      }
      if (!active) throw redirect({ to: "/onboarding" });
      orgId = active;
    }

    // Resolved absolute path with an arbitrary (possibly nested) tab tail.
    // `href` navigates verbatim; the target's first segment is the org, not
    // `containers`, so it re-enters the /orgs/$orgId setActive guard, not this route.
    throw redirect({
      href: `/orgs/${orgId}/containers/${containerId}${tail}${location.searchStr}`,
    });
  },
  component: () => null,
});


// Back-compat: old bookmarks and external links that used the bare
// /<orgId>/... shape (before the /orgs/ prefix was added). Catches any
// unmatched $legacyOrgId at the protected root and redirects to /orgs/$id/....
// Static routes (/onboarding, /login, /containers/..., /organization/...) all
// match first because they are explicit paths, so this only fires for strings
// that look like an org id (the old URL pattern).
//
// `orgs` itself is reserved: a stray /orgs URL (or an /orgs/... path whose
// tail matches no real child) falls through to this catch-all, and rewriting
// it to /orgs/orgs... appends a segment on every pass — an infinite redirect
// loop that grows the URL. Bounce those to "/" instead, where the root
// redirect resolves the viewer's actual org.
const legacyBareOrgRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "$legacyOrgId/$",
  beforeLoad: ({ params, location }) => {
    const { legacyOrgId, _splat } = params as { legacyOrgId: string; _splat?: string };
    if (legacyOrgId === "orgs") {
      throw redirect({ to: "/" });
    }
    const tail = _splat && _splat.length > 0 ? `/${_splat}` : "";
    throw redirect({
      href: `/orgs/${legacyOrgId}${tail}${location.searchStr}`,
    });
  },
  component: () => null,
});

// Org sentinels: /organization/<page> is a link that knows the destination but
// NOT which org — there's no id to resolve it from (unlike a container). So the
// frontend throws up an org picker; the user's choice mints the canonical
// /orgs/$orgId/<page>. These MUST be explicit static routes, not a splat: `billing`
// et al. are real /orgs/$orgId children, so a `/organization/$` splat would lose to
// `$orgId=organization`/`billing`. A fully-static `/organization/billing` beats
// the dynamic `$orgId/billing`, so it wins. A single-org user is auto-forwarded
// by the component (no pointless picker). Add a route here per linkable org page.
const orgSentinelPaths = ["/organization/billing", "/organization/members", "/organization/usage"] as const;
// Parented on the root (not protectedRoute) so the picker renders full-screen —
// no dashboard sidebar bound to a not-yet-chosen org. Its own requireAuth keeps
// it behind login. Same treatment as /choose-organization and /no-access.
const organizationSentinelRoutes = orgSentinelPaths.map((path) =>
  createRoute({ getParentRoute: () => rootRoute, path, beforeLoad: requireAuth, component: OrgPickerRedirect }),
);

// The bare org picker: where "/" sends a multi-org user with no valid active org.
// Full-screen, auth-guarded, outside the dashboard shell.
const chooseOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/choose-organization",
  beforeLoad: requireAuth,
  component: OrgPickerRedirect,
});

// --- Admin layout (shows 404 for unauthenticated or non-admin users) ------

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "superadmin",
  beforeLoad: requireAdminOrNotFound,
  component: AdminLayout,
});

const adminOrgsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/organizations",
  component: AdminOrgsPage,
});

const adminOrgDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/organizations/$orgId",
  component: AdminOrgDetailPage,
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === "string" &&
    ["members", "containers", "invitations", "apps", "billing"].includes(search.tab)
      ? { tab: search.tab }
      : {},
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/users",
  component: AdminUsersPage,
});

const adminInfrastructureRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/infrastructure",
  component: AdminInfrastructurePage,
});

const adminContainerDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/infrastructure/$containerId",
  component: AdminContainerDetailPage,
  // `?tab` selects the active tab (Overview default). `?bundles` is set only from
  // the Named Warm Pool "Manage" flow, where the brain is a parked pool brain and
  // the admin bundle routes apply (surfaces the Bundles tab).
  validateSearch: (search: Record<string, unknown>): { bundles?: boolean; tab?: string } => ({
    ...(search.bundles ? { bundles: true } : {}),
    ...(typeof search.tab === "string" ? { tab: search.tab } : {}),
  }),
});

const adminCostsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/costs",
  component: AdminCostsPage,
});

const adminLLMUsageRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/llm-usage",
  component: AdminLLMUsagePage,
});

const adminPermissionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/permissions",
  component: AdminPermissionsPage,
});

const adminGchatCustomRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/gchat-custom",
  component: AdminGchatCustomPage,
});

const adminSlackRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/slack",
  component: AdminSlackConnectionsPage,
});

const adminNamedWarmPoolRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/named-warm-pool",
  component: AdminNamedWarmPoolPage,
});

const adminWaitlistRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/waitlist",
  component: AdminWaitlistPage,
});

const adminSubdomainsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/subdomains",
  component: SubdomainAdminPage,
});

const adminBillingGrantsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/billing-grants",
  component: AdminBillingGrantsPage,
});

const adminBillingConfigRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/billing-config",
  component: AdminBillingConfigPage,
});

const adminAgentKeysRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/agent-keys",
  component: AdminAgentKeysPage,
});

const adminExperimentsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/superadmin/experiments",
  component: AdminExperimentsPage,
});

// ============================================================================
// Route tree
// ============================================================================

const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  verifyRoute,
  finishSetupRoute,
  onboardingLayout.addChildren([
    onboardingIndexRoute,
    onboardingWelcomeRoute,
    onboardingOrgRoute,
    onboardingProvisioningRoute,
    onboardingConnectRoute,
  ]),
  bannedRoute,
  noAccessRoute,
  legacyRootRoute,
  chooseOrgRoute,
  ...organizationSentinelRoutes,
  acceptInvitationRoute,
  privacyPolicyRoute,
  termsAndConditionsRoute,
  connectorLinkRoute,
  protectedRoute.addChildren([
    orgRoute.addChildren([
      dashboardRoute,
      containerLayoutRoute.addChildren([
        containerIndexRoute,
        containerConnectionRoute,
        containerChatRoute,
        containerBrainChatRedirectRoute,
        containerLogsRoute,
        containerUsageRoute,
        containerGeneralRoute,
        containerIntegrationsRoute,
        containerConnectorRoute,
        containerConnectorRedirectRoute,
        containerProxyRedirectRoute,
        containerChannelsRoute,
        containerPeopleRoute,
        containerCanUseRoute,
        containerExtrasRoute,
        containerAppsRoute,
        containerSharingRoute,
        containerAccessRoute,
        containerActivityRoute,
        containerVaultRoute,
        containerModelsRoute,
      ]),
      usageRoute,
      billingRoute,
      mockCheckoutRoute,
      membersRoute,
      accessRoute,
      auditRoute,
      manageIntegrationRoute,
      orgSettingsRoute,
      orgConnectionsRoute,
      orgConnectionRoute,
      orgIntegrationsRoute,
      orgModelsRoute,
      legacyManageRoute,
    ]),
    legacyContainerRoute,
    legacyBareOrgRoute,
  ]),
  adminRoute.addChildren([
    adminOrgsRoute,
    adminOrgDetailRoute,
    adminUsersRoute,
    adminInfrastructureRoute,
    adminContainerDetailRoute,
    adminCostsRoute,
    adminLLMUsageRoute,
    adminPermissionsRoute,
    adminSubdomainsRoute,
    adminGchatCustomRoute,
    adminSlackRoute,
    adminNamedWarmPoolRoute,
    adminWaitlistRoute,
    adminBillingGrantsRoute,
    adminBillingConfigRoute,
    adminAgentKeysRoute,
    adminExperimentsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteErrorBoundary,
  defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
